import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { fetchFastApi } from "../../api/fastapi";
import { useHookCache } from "../../lib/hookCache";
import { type AnalyticsColumnResult } from "../../models/audit/analyticsColumnResult";
import { type AuditSession } from "../../models/audit/auditSession";
import { type Citation } from "../../models/audit/citation";
import {
  servedDeletedWorkflowSteps,
  type WorkflowDeletion,
} from "../../pages/audit/auditWorkPaperViewV2/lib/deletedWorkflowSteps";
import {
  composeDeterministicSteps,
  resolvePopulation,
  type DeterministicOperationRow,
  type DeterministicStepList,
  type PopulationStatement,
} from "../../pages/audit/auditWorkPaperViewV2/lib/deterministicSteps";
import {
  useAnalyticsRunSteps,
  useAnalyticsRunManifest,
  useAnalyticsStatus,
} from "../useAnalyticsWorkflows";
import { useAnalyticsResultData } from "./useAnalyticsResultData";
import {
  useAnalyticsStepCards,
  type StepCardDocuments,
} from "./useAnalyticsStepCards";
import type { AnalyticsOwner } from "../../lib/analyticsOwner";

interface UseDeterministicStepsProps {
  auditSession: AuditSession;
  /** The fold-out's selection id. Anything but an analytics one leaves this idle. */
  checkResultId: string | undefined;
  sourceRowId: string | undefined;
  analyticsOwner: AnalyticsOwner | undefined;
}

export interface UseDeterministicStepsResult {
  /** True when the selection is an analytics verdict, so the tab applies at all. */
  isAnalytics: boolean;
  steps: DeterministicStepList;
  population: PopulationStatement;
  isLoading: boolean;
  /**
   * True where a read the list is assembled from failed, so the tab knows its
   * account is off for a reason that is not the run's.
   *
   * Meaningful only where `isAnalytics`, and false otherwise: no read behind it
   * fires until the selection names a verdict — the manifest and trail keyed on
   * the verdict id, the run's operations keyed on a column the verdict has to
   * name before that read enables — so a selection naming none attempts nothing
   * and can fail at nothing.
   *
   * Both reads answer to the same selection but ride different keys. The
   * manifest and trail ride the verdict, on its id; the operations ride the
   * cell, on control, column and row with no verdict id. So once a newer run is
   * published to the cell the operations refresh to it while the verdict-keyed
   * halves still describe the run the selection named, and the two can describe
   * different runs.
   *
   * Surfaced rather than swallowed because every one of those failures is
   * silent and wrong in the same direction the verdict's own step log guards
   * against: an empty `phases` renders an eight-operation run as two bookends,
   * and an unreadable card set leaves the tab unable to say which publish steps
   * the run gave no evidence — and it would go on to count either.
   */
  stepsUnreadable: boolean;
  /**
   * Whether the column's workflow still exists, and where it does not, the copy
   * this tab and its preview show in its place — composed once so the two cannot
   * word it differently.
   *
   * **Keyed on the column's status and never on a read failing.** A deleted
   * workflow does fail `/analytics/run-steps`, which reads the instance the
   * deletion removes, but it fails as a 502 — the `upstream_unavailable` an
   * engine outage also answers. A tab that inferred the state from that read
   * would call a live workflow deleted whenever the engine is down, so
   * `stepsUnreadable` above — which is a report on a read — can never stand in
   * for this. The verdict and its trail are Midship's own records and survive
   * the deletion, so they cannot report it either.
   *
   * **The operations read waits on this.** It is not issued until the status
   * answers, and not at all where the status says deleted: issued alongside
   * the status, it would fail and toast on the screen that explains the
   * deletion.
   *
   * `unsettled` until the status answers, so neither surface draws evidence on
   * the strength of a question still in the air. It is not reachable once the
   * status has answered: a later refetch of it leaves the answer already in
   * hand, so a card being read cannot be pulled out from under an auditor.
   *
   * `intact` where the status read failed, and deliberately: the run's steps are
   * the run's own account and are whole, the failure is the column's, and the
   * review pane beside this one already reports it. A second notice here would
   * deny an account that stands.
   *
   * `intact` too where the verdict is not in hand, whatever the status says: a
   * tab with no verdict is not describing a run at all, and the existing empty
   * and unreadable states are what render there, and they are true.
   *
   * The deleted arm's card is `pending` while the run manifest's first read is
   * out. The manifest serves its copy for any bound column, deleted or not, so
   * it is read only here, behind the status's own `workflow_deleted`.
   */
  workflowDeletion: WorkflowDeletion;
  /** The step whose card the viewer is showing, or null before anything is picked. */
  selected: DeterministicOperationRow | null;
  selectStep: (row: DeterministicOperationRow) => void;
  /** Close the open card, for a host handing the viewer to something else. */
  clearStep: () => void;
  /** Move a step into or out of the review pane's key-steps section. */
  toggleStar: (row: DeterministicOperationRow) => Promise<void>;
  /**
   * Move a step into or out of the review pane's key-steps section by its card's
   * own citation, for a caller holding the resolved card rather than a step row.
   *
   * The write the star makes, unpicked from the row lookup: the review pane's
   * cards resolve straight from the verdict's trail, so a caller with a card in
   * hand writes the flag without the run-steps read a row is composed from — the
   * read that is empty while it is in flight or has failed. `toggleStar` is this
   * with the citation taken off the row it was handed.
   */
  toggleStarForCitation: (citation: Citation) => Promise<void>;
}

/**
 * Whether the card read this tab's count rests on failed to land.
 *
 * Answered arm by arm, and deliberately not as "anything but `read`": a verdict
 * naming no run establishes nothing about cards either, but there are no cards
 * to have read and the tab's account of the run is whole without them. Saying
 * the run's steps could not be read there is a different false statement from
 * the one this guard exists to prevent.
 */
function stepCardsUnreadable(cardDocuments: StepCardDocuments): boolean {
  switch (cardDocuments.kind) {
    case "unreadable":
      return true;
    case "read":
    case "noRun":
      return false;
    default: {
      const unhandled: never = cardDocuments;
      return unhandled;
    }
  }
}

/** One citation's in-flight star writes, and what the server last accepted. */
interface StarWrite {
  /** Presses not yet settled. The entry lives only while this is above zero. */
  pending: number;
  /** The last flag the server accepted. */
  confirmed: boolean;
  /** The previous press on this citation. Never rejects. */
  chain: Promise<void>;
}

const EMPTY_STEPS: DeterministicStepList = {
  rows: [],
  inputs: [],
  outputs: [],
  stepCount: 0,
  loggedOperationCount: 0,
  // Nothing was composed, so nothing established what a count here would be
  // over. The panel reaches its empty state before reading it either way.
  stepCountUnestablished: true,
  runDate: null,
};

/**
 * Everything the Deterministic Steps tab reads, and the one thing it writes.
 *
 * Three reads, because no one of them holds the list: the run's own operations
 * come from `/analytics/run-steps`, Midship's two bookend rows and the step ids
 * ride the verdict, and the cards behind the rows
 * are resolved by the review pane's own resolver — the same one, not a second
 * copy, so a step starred here and a card placed there cannot disagree about
 * which card the step has.
 *
 * **A fourth read answers a question none of those three can: whether the
 * workflow still exists.** The column's status is the only report of a
 * deletion, and the run's operations are not read until it has answered, nor
 * where it answers deleted — see `workflowDeletion`.
 *
 * **The one write is the star, and it is deliberately the only one.** The tab is
 * transparency: it lists the run's operations, less the publish steps that
 * produced no exceptions output — those are given no card, so a row for one
 * would open no evidence. An auditor cannot correct the population figure here,
 * cannot reassign an output's role and has nothing to pin — those belong to the
 * surfaces that own those facts, and offering them here would let an auditor
 * edit the account of a run from inside the account of the run.
 */
export function useDeterministicSteps({
  auditSession,
  checkResultId,
  sourceRowId,
  analyticsOwner,
}: UseDeterministicStepsProps): UseDeterministicStepsResult {
  const {
    analyticsResultId,
    analyticsResult,
    isLoading: analyticsResultLoading,
    error: analyticsResultError,
    column,
  } = useAnalyticsResultData({
    auditSession,
    checkResultId,
  });

  // The column's own status, which is the only place a deleted workflow is
  // reported.
  const { data: status, isLoading: statusPending } = useAnalyticsStatus(
    analyticsOwner,
    column?.analyticsColumn?.id,
  );
  const workflowDeleted = status?.status === "workflow_deleted";
  // Asked only of the first read: the pending flag rises again on every refetch
  // while the answer stays cached, and holding on it would pull a card an
  // auditor is reading out from under them each time one fires.
  const workflowFateUnsettled = status === undefined && statusPending;

  // Held until the status answers and withheld where it says deleted: the route
  // 502s for a deleted workflow, so issuing it alongside the status would fail
  // and toast on the first open of every deleted column. A failed status read
  // releases it, because that failure is the column's and the run's steps stand.
  const runStepsWithheld = workflowDeleted || workflowFateUnsettled;
  const {
    data: runSteps,
    isLoading: runStepsLoading,
    error: runStepsError,
  } = useAnalyticsRunSteps(
    analyticsOwner,
    column?.analyticsColumn?.id,
    sourceRowId,
    { enabled: !runStepsWithheld },
  );
  // The run half of the deleted-state copy, and the only payload describing
  // this row's run — the status read reports the column's most recent run,
  // which is not always this row's. Withheld until the status says deleted: on
  // the steps tab the review pane that also reads it is not mounted, so a live
  // workflow would pay a cold round trip for copy it never shows. Enabled on
  // the render the status first answers deleted, which registers the read on
  // that same render, so the card never passes through the date-free fallback.
  const { data: runManifestRead, isLoading: runManifestLoading } =
    useAnalyticsRunManifest(
      analyticsOwner,
      column?.analyticsColumn?.id,
      sourceRowId,
      { enabled: workflowDeleted },
    );

  const { resolved, cardDocuments } = useAnalyticsStepCards(
    auditSession.documents,
    analyticsResult,
  );

  const steps = useMemo(
    () =>
      analyticsResult === undefined
        ? EMPTY_STEPS
        : composeDeterministicSteps({
            phases: runSteps?.phases ?? [],
            manifest: analyticsResult.runManifest,
            trail: analyticsResult.auditorTrail,
            cards: resolved,
            cardDocuments,
          }),
    [analyticsResult, runSteps, resolved, cardDocuments],
  );

  const population = useMemo(
    () => resolvePopulation(analyticsResult ?? null),
    [analyticsResult],
  );

  const workflowDeletion = useMemo<WorkflowDeletion>(() => {
    if (workflowDeleted && analyticsResult !== undefined) {
      return {
        kind: "deleted",
        copy: servedDeletedWorkflowSteps(
          status?.deletedWorkflowCopy,
          // Asked of the first read only, as the status is: a refetch with the
          // copy already in hand must not pull the card back to pending. An
          // answer in hand stays the answer while a later refetch fails, as the
          // cache keeps it; a first read that failed, found no run or was never
          // issued has none.
          runManifestRead === undefined
            ? runManifestLoading
              ? { kind: "pending" }
              : { kind: "unavailable" }
            : {
                kind: "answered",
                copy: runManifestRead.deletedWorkflowCopy,
              },
          column?.analyticsColumn?.workflowBinding?.automationWorkflowId ??
            null,
          {
            warnOnMissingColumn: !statusPending,
            warnOnMissingRun: !statusPending,
          },
        ),
      };
    }
    // Asked after the deleted arm rather than before it, so an answer already in
    // hand settles the question whatever a later read of it is doing.
    return workflowFateUnsettled ? { kind: "unsettled" } : { kind: "intact" };
  }, [
    status,
    statusPending,
    workflowDeleted,
    workflowFateUnsettled,
    column,
    analyticsResult,
    runManifestRead,
    runManifestLoading,
  ]);

  // The id rather than the row, so the selection survives a refetch that rebuilds
  // every row object — starring a step must not close the card it starred.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = useMemo(
    () => steps.rows.find((row) => row.key === selectedKey) ?? null,
    [steps, selectedKey],
  );

  const selectStep = useCallback((row: DeterministicOperationRow) => {
    setSelectedKey(row.key);
  }, []);

  const clearStep = useCallback(() => {
    setSelectedKey(null);
  }, []);

  const { setEntry } = useHookCache();

  /**
   * Per-citation star bookkeeping. `confirmed` is the last flag the server
   * accepted, `pending` counts presses not yet settled, and `chain` serialises
   * the writes.
   *
   * **The writes are serialised because settle order is not apply order.** Two
   * presses in flight together can reach the server in one order and answer in
   * the other, and nothing in a response says which won — so a client that
   * reconciles to whichever answered last can leave the star on a value the
   * server took and then overwrote. Chaining each press behind the previous one
   * makes press order, apply order and settle order the same order, which is
   * what lets `confirmed` be read as the server's current value rather than a
   * guess about it.
   *
   * A press therefore never undoes itself from the flag it captured: mid-burst
   * that flag is another press's optimistic value, and restoring it would leave
   * the star lit off nothing the server ever held.
   */
  const starWritesRef = useRef(new Map<string, StarWrite>());

  const writeStarFlag = useCallback(
    (citationId: string, isKey: boolean) => {
      setEntry<AnalyticsColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "analytics-result",
          analyticsResultId ?? "",
        ],
        (prev) => {
          const trail = prev.auditorTrail;
          if (trail == null) return prev;
          // Asked as "does any copy disagree" rather than "does one agree", so a
          // citation carried in more than one step cannot leave a stale copy
          // behind on the strength of a matching sibling.
          const stale = trail.steps.some((step) =>
            step.citations.some(
              (c) => c.id === citationId && (c.isKey === true) !== isKey,
            ),
          );
          if (!stale) return prev;
          return {
            ...prev,
            auditorTrail: {
              ...trail,
              steps: trail.steps.map((step) => ({
                ...step,
                citations: step.citations.map((c) =>
                  c.id === citationId ? { ...c, isKey } : c,
                ),
              })),
            },
          };
        },
      );
    },
    [auditSession.id, analyticsResultId, setEntry],
  );

  const toggleStarForCitation = useCallback(
    async (citation: Citation) => {
      const isKey = citation.isKey !== true;

      const writes = starWritesRef.current;
      // Seeded from the citation only when nothing is in flight: mid-burst the
      // cached flag is this hook's own optimistic value rather than the
      // server's, and seeding from it would make a guess the baseline.
      const write = writes.get(citation.id) ?? {
        pending: 0,
        confirmed: citation.isKey === true,
        chain: Promise.resolve(),
      };
      write.pending += 1;
      writes.set(citation.id, write);

      // The flag is written into the cached verdict before the request goes out,
      // because the next press reads its value from there. Two presses inside
      // one round trip would otherwise both compute the flag from the same
      // stale citation, send the same value, and each toast as though it had
      // toggled — the second is absorbed with nothing on screen to say so.
      //
      // One cache entry and not two: the row's star and the review pane's
      // key-steps section are both read off this verdict's trail, so the write
      // that corrects one corrects the other.
      writeStarFlag(citation.id, isKey);

      // Queued behind this citation's previous press rather than raced against
      // it. The press is already on screen, so the wait costs the auditor
      // nothing and buys an order the server and this client agree on.
      const settled = write.chain.then(async () => {
        try {
          await fetchFastApi({
            method: "PUT",
            path: `/citations/${citation.id}`,
            body: { citation: { ...citation, isKey } },
            // Without this an error body is returned rather than thrown, so a
            // write that did not happen reaches the success toast and the
            // optimistic star is never undone. Opted into per call, as the other
            // writes that must not fail silently already do; the default is left
            // alone.
            throwOnErrorEnvelope: true,
          });
          write.confirmed = isKey;
          // No re-read follows. The write above is the newest one this citation
          // has, and it was accepted, so the server holds what was just sent —
          // refetching the verdict buys nothing and costs both panes:
          // `invalidate` raises `isLoading` while the old data is still cached,
          // and the steps list and review tab read that before data, so the list
          // unmounts and comes back scrolled to the top, and a re-read that
          // fails replaces a saved star with an error screen.
          toast.success(
            isKey
              ? "Step will be included in workpaper"
              : "Step removed from workpaper",
          );
        } catch (error) {
          console.error("Error toggling step card citation:", error);
          toast.error("Failed to update step");
        }
      });
      // Held as the next press's predecessor. It never rejects — the failure is
      // reported inside — so a refused write stalls neither the queue nor the
      // reconciliation behind it.
      write.chain = settled;
      await settled;

      write.pending -= 1;
      // The last press to settle owns the flag: while another is queued the
      // display belongs to it, and correcting here would fight a write that has
      // not been sent yet.
      if (write.pending === 0) {
        writes.delete(citation.id);
        writeStarFlag(citation.id, write.confirmed);
      }
    },
    [writeStarFlag],
  );

  const toggleStar = useCallback(
    async (row: DeterministicOperationRow) => {
      // A step with no card has nothing to include in the workpaper: the star is
      // the citation's own flag, and there is no second place to record it.
      if (row.citation === null) return;
      await toggleStarForCitation(row.citation);
    },
    [toggleStarForCitation],
  );

  const isAnalytics = !!analyticsResultId;

  return {
    isAnalytics,
    steps,
    population,
    // The step list is composed from two reads on different keys: the verdict
    // carries the run's bookends and trail on its id, and the run's operations
    // come from the cell-keyed run-steps read (control, column and row, no
    // verdict id). Until both answer, or where either failed, the tab has
    // nothing to say about how many steps ran — and an empty list here reads as
    // a run that recorded none. The operations riding the cell can describe a
    // different run than the verdict-keyed bookends once a newer one is
    // published there.
    isLoading: analyticsResultLoading || runStepsLoading,
    // The cards join those failures, because the list is short by construction
    // where they cannot be established too: an unreadable card set leaves the
    // tab unable to say which publish steps the run gave no evidence, and a
    // count taken over rows nothing could be established about is a claim the
    // tab cannot make.
    //
    // Asked of the card state alone and never of a row's own presence: that
    // presence coarsens a verdict naming no run into the same `unreadable` a
    // failed read wears, so reading it back would head that verdict's own
    // correctly drawn rows with the notice that the run's steps could not be
    // read.
    //
    // Gated on the selection being an analytics one, because every read the
    // arms rest on is: an idle hook holds an undefined verdict, which the card
    // resolver reports as unreadable without a read having been attempted.
    // Ungated, the field would call the run's steps unreadable for every agent
    // and attribute-test selection.
    //
    // A deleted status with no verdict to describe joins them: the run's
    // operations were withheld on it, so the list is short by exactly as much
    // as if their read had failed.
    stepsUnreadable:
      isAnalytics &&
      (analyticsResultError !== null ||
        runStepsError !== null ||
        (workflowDeleted && workflowDeletion.kind !== "deleted") ||
        stepCardsUnreadable(cardDocuments)),
    workflowDeletion,
    selected,
    selectStep,
    clearStep,
    toggleStar,
    toggleStarForCitation,
  };
}
